import { _ } from "../../../core/polyglot/polyglot.js";
import { findInterestingNodes } from "../../../models/keyFigure/formula/visitors/InterestingNodesFinderFormulaVisitor.js";
import { isNullOrEmpty } from "../../../core/helpers/string-util.js";
import { makeMonitorRepository } from "../../../core/repositories/repositoryBuilders.js";
import {
	notBlank,
	validNumber,
} from "../../../core/widgets/forms/validators.js";
import accountingDocument from "../../shared/accountingDocument.js";
import ChartOfAccounts from "../../../models/chartOfAccounts/ChartOfAccounts.js";
import dimensionPickerWidget from "../../../core/widgets/dimensionPickerWidget.js";
import getKeyFigureService from "../../../core/services/getKeyFigureService.js";
import hyphenator from "../../../core/hyphenator.js";
import KeyFigureCalculationService from "../editor/KeyFigureCalculationService.js";
import KeyFigureChartWidget from "../shared/KeyFigureChartWidget.js";
import KeyFigureComponentsWidget from "./KeyFigureComponentsWidget/KeyFigureComponentsWidget.js";
import KeyFigureData from "../../../models/keyFigure/KeyFigureData.js";
import KeyFigureDefinitionWidget from "./KeyFigureDefinitionWidget.js";
import keyFigureDescriptionWidget from "./keyFigureDescriptionWidget.js";
import KeyFigurePresetService from "../../../core/services/KeyFigurePresetService.js";
import KeyFigureResultService from "../../../core/services/KeyFigureResultService.js";
import KeyFigureSelectionWidget from "../../../core/widgets/keyFigures/KeyFigureSelectionWidget/KeyFigureSelectionWidget.js";
import keyFigureValueFormatter from "../../../models/keyFigure/rendering/keyFigureValueFormatter.js";
import keyFigureValueWidget from "../shared/keyFigureValueWidget.js";
import numbro from "../../../core/numbroWrapper.js";
import openDriverKeyFigureCreationDialog from "../../../core/widgets/keyFigures/openDriverKeyFigureCreationDialog.js";
import optionsBag from "../../../models/options/optionsBag.js";
import popoverActionButtonWidget from "../../../core/widgets/popoverActionButtonWidget.js";
import previousPeriodPickerWidget from "../shared/actions/previousPeriodPickerWidget.js";
import session from "../../../core/session.js";
import visibleKeyFigures from "../../../models/keyFigure/preset/visibleKeyFigures.js";

/**
 * `keyFigureDocument` displays informations, current and historical values about a keyfigure
 * and its components. It shows also a menu to edit this keyfigure and to add custom KPIs.
 */
function keyFigureDocument(spec, my) {
	spec ||= {};
	my ||= {};

	const that = accountingDocument(spec, my);

	that.title = function (html) {
		if (my.keyFigurePicker.hasSelection()) {
			html.span(
				html.b(hyphenator.hyphenate(getSelectedKeyFigure().getName())),
			);
		} else {
			html.span(_("KPI"));
		}
	};

	that.subtitle = function (html) {
		if (!my.keyFigurePicker.hasSelection()) {
			return;
		}

		html.span(
			_("{kpiSubtitle} for {dimensionName}", {
				kpiSubtitle: getSelectedKeyFigure().getSubtitle(),
				dimensionName: my.dimensionPicker
					.getSelectedDimensionName()
					.toString()
					.toLowerCase(),
			}),
		);
	};

	that.documentId = "kpiDocument";

	that.help = function () {
		return [
			{
				title: _("KPI"),
				content: _(
					"This page displays the current value of a chosen KPI, some reference info, a graph showing the development of the actual KPI and finaly the components that effect the outcome for the chosen KPI.",
				),
			},
			{
				title: _("Current value"),
				content: _(
					"The value is displayed in relation to a goal value (if set). Compared to previous period an arrow also given to represent if the current value is moving closer to the set goal value or if it is moving away from it.",
				),
				element: "kpi-current-value",
			},
			{
				title: _("Progress"),
				content: _(
					"This graph show how the current value is developing over time.",
				),
				element: "kpi-chart",
			},
			{
				title: _("Components"),
				content: _(
					"A Key Performance Index is often a ratio of two or more components from the bookkeeping. These components are displayed in a table below the graph where one can see the development of these components to understand the movement of the KPI.",
				),
				element: "kpi-components",
			},
			{
				title: _("Description"),
				content: _(
					"This is a explanatory text that will give you a general understanding of what the KPI is measuring.",
				),
				element: "kpi-description",
			},
			{
				title: _("How it is calculated"),
				content: _("Shows you the algorithm for the KPI"),
				element: "kpi-description",
			},
		];
	};

	my.keyFigureResultService = new KeyFigureResultService({
		companyId: session.getCompanyId(),
		keyFigureService: getKeyFigureService(),
	});

	my.keyFigureCalculationService = new KeyFigureCalculationService({
		companyId: session.getCompanyId(),
	});

	my.keyFigurePresetService = new KeyFigurePresetService({
		companyId: session.getCompanyId(),
		keyFigureService: getKeyFigureService(),
	});

	const antiForgeryToken = session.getAntiForgeryToken();

	my.keyFigureDataRepository = makeMonitorRepository({
		route: "/Accounting2/Company/#companyId/FiscalYear/#fiscalYearId/KeyFigureData/",
		model: KeyFigureData,
		defaultFilters: {
			companyId: session.getCompany().getLegacyId(),
		},
		antiForgeryToken,
	});

	my.chartOfAccountsRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/ChartOfAccounts/#id",
		model: ChartOfAccounts,
		defaultFilters: {
			companyId: session.getCompany().getLegacyId(),
			id: session.getCurrentChartOfAccountsId(),
		},
		antiForgeryToken,
	});

	my.keyFigurePicker = new KeyFigureSelectionWidget({
		onItemSelected: () => {
			my.keyFigurePickerPopover.hide();
			my.updateUrl();
		},
	});

	my.keyFigurePickerPopover = popoverActionButtonWidget({
		label: _("Select KPI"),
		popoverHeader: _("Select KPI"),
		popoverContent: my.keyFigurePicker,
		buttonType: "btn-primary btn-block",
		dataTestName: "select-kpi",
	});

	my.previousPeriodPicker = previousPeriodPickerWidget();

	my.previousPeriodPicker.onPeriodChange(() => {
		my.updateUrl();
	});

	my.dimensionPicker = dimensionPickerWidget({
		dimensionTree: session.getDimensionTree(),
	});

	my.dimensionPicker.onDimensionSelected.register(() => {
		my.updateUrl();
	});

	my.keyFigureValue = keyFigureValueWidget();

	my.keyFigureChart = new KeyFigureChartWidget({
		showTooltip: false,
		showDataLabels: true,
	});

	my.keyFigureChart.initialize();

	my.keyFigureChart.onPlotbandHovered.register((plotband) => {
		my.keyFigureValue.setCurrentValue(plotband.options.data);
	});

	my.keyFigureChart.onPlotbandExited.register(() => {
		my.keyFigureValue.setCurrentValue();
	});

	my.keyFigureChart.onPlotbandClick.register((plotBand) => {
		session.redirectToPeriod(plotBand.options.period);
	});

	my.keyFigureChart.onPointClick.register((point) => {
		session.redirectToPeriod(point.period);
	});

	my.keyFigureComponents = new KeyFigureComponentsWidget({
		selectedPeriod: session.getPeriod(),
		dimensionTree: session.getDimensionTree(),
	});

	my.keyFigureDescription = keyFigureDescriptionWidget();

	my.keyFigureDefinition = new KeyFigureDefinitionWidget({
		dimensionTree: session.getDimensionTree(),
	});

	that.actions = [
		my.rightMenuSection("", (html) => {
			my.renderSelectKeyFigureButtonOn(html);
			my.renderEditKeyFigureSectionOn(html);
			my.renderCreateKeyFigureButtonOn(html);
			my.renderCreateDriverKeyFigureButtonOn(html);
		}),
		my.rightMenuSection(
			_("Number of previous periods"),
			my.previousPeriodPicker,
		),
		my.rightMenuSection(_("Dimension"), my.dimensionPicker),
		(html) => {
			const keyFigure = getSelectedKeyFigure();
			if (
				!keyFigure ||
				keyFigure.isFinancialKeyFigure() ||
				!session
					.getCurrentUser()
					.canEditKeyFigureDataFor(session.getCompany())
			) {
				return;
			}

			html.render(
				my.rightMenuSection(
					_("KPI data"),
					html.a(
						{
							klass: "btn btn-default btn-block",
							click: () =>
								my.editKeyFigureDataForCurrentFiscalYear(),
						},
						_("Enter values"),
					),
				),
			);
		},
	];

	/**
	 * Change keyfigure or type of timeframe/period to show.
	 *
	 * All options are optional and we will use the
	 * current state as default.
	 *
	 * spec.keyFigure                 Identifier for keyfigure to show
	 * spec.periodLength        The length of the 'total timeframe/period' to show. A number between 1-12
	 *
	 * _NOTE:_ We also update period and company from session
	 *
	 */
	// eslint-disable-next-line no-shadow -- bad practice, we should fix this
	that.show = async function (spec = {}) {
		that.showUpdating();

		const periodLength = spec.periodLength
			? parseInt(spec.periodLength, 10)
			: null;
		if (spec.dimensionId) {
			const dimension = session
				.getDimensionTree()
				.findById(spec.dimensionId);
			my.dimensionPicker.setSelectedDimension(dimension);
		}

		if (periodLength) {
			my.previousPeriodPicker.setSelected(periodLength);
		}

		const presets = await my.keyFigurePresetService.findAllVisiblePresets();
		const keyFigures = visibleKeyFigures.getKeyFigures();
		my.keyFigurePicker.setup({ keyFigures, presets });

		my.selectDefaultKeyFigure({ keyFigureId: spec.keyFigure, presets });

		// Don't update document if no key figure is selected
		if (!my.keyFigurePicker.hasSelection()) {
			that.showEmpty();
			return;
		}

		const numPeriods = my.previousPeriodPicker.getSelected();
		const lastPeriods = session
			.getCompany()
			.getLatestPeriods(numPeriods, session.getPeriod());

		const [result, chartOfAccounts] = await Promise.all([
			my.keyFigureResultService.getValuesForPeriods({
				keyFigures: [getSelectedKeyFigure()],
				periods: lastPeriods,
				dimensionId: my.dimensionPicker.getSelectedDimensionId(),
			}),
			my.chartOfAccountsRepository.get(),
		]);

		// Force to materialize the formula with the option 'getAccountName'
		const formula = result.getFirstKeyFigure().getFormula({
			getAccountName: (id) => chartOfAccounts.getAccountName(id),
		});

		my.keyFigureChart.setResult(result);
		my.keyFigureValue.setResult(result);

		await my.setupKeyFigureComponents({
			keyFigureResult: result,
			lastPeriods,
		});

		my.keyFigureDescription.setKeyFigure(getSelectedKeyFigure());

		my.keyFigureDefinition.setFormula(formula);

		that.showContent();
		my.updateActions();
	};

	my.renderContentOn = function (html) {
		html.render(
			my.section({
				content: function (html) {
					html.div(
						{
							klass: "col-lg-4 col-md-4 col-sm-12 col-sm-nopadding nopadding-left",
							"data-help": "kpi-current-value",
						},
						html.h2(_("Current value")),
						my.keyFigureValue,
					);

					html.div(
						{
							klass: "col-lg-8 col-md-8 col-sm-12 col-sm-nopadding nopadding-right",
							"data-help": "kpi-chart",
						},
						html.h2(_("History")),
						my.keyFigureChart,
					);
				},
			}),
			my.section({
				dataHelpName: "kpi-components",
				title: _("Components"),
				content: my.keyFigureComponents,
			}),
			my.section({
				dataHelpName: "kpi-description",
				content: function (html) {
					html.div(
						{ klass: "row" },
						html.div(
							{ klass: "col-lg-6 col-md-6 col-sm-6" },
							html.h2(_("Description")),
							my.keyFigureDescription,
						),
						html.div(
							{ klass: "col-lg-6 col-md-6 col-sm-6" },
							html.h2(_("How it is calculated")),
							my.keyFigureDefinition,
						),
					);
				},
			}),
		);
	};

	my.renderSelectKeyFigureButtonOn = function (html) {
		html.render(my.keyFigurePickerPopover);
	};

	my.keyFigureEditButtonRenderer = function (keyFigure) {
		return (html) =>
			html.a(
				{
					klass: "btn btn-default btn-block",
					"data-test": "edit-kpi",
					href: my.linkTo(
						`kpi-editor?keyFigureId=${keyFigure.getId()}`,
					),
				},
				_("Edit key figure"),
			);
	};

	my.canEditKeyFigure = function (keyFigure) {
		const kpiEditorIsActive = optionsBag
			.fromId("keyfigure-editor")
			.isActive();

		const userCanEditKeyFigure = session
			.getCurrentUser()
			.canEditKeyFiguresFor(session.getCompany());

		return keyFigure && kpiEditorIsActive && userCanEditKeyFigure;
	};

	my.canCreateCustomKeyFigures = function () {
		if (!optionsBag.fromId("keyfigure-editor").isActive()) {
			return false;
		}

		return session
			.getCurrentUser()
			.canEditKeyFiguresFor(session.getCompany());
	};

	my.renderEditKeyFigureSectionOn = function (html) {
		const keyFigure = getSelectedKeyFigure();

		if (!my.canEditKeyFigure(keyFigure)) {
			return;
		}

		html.render(my.keyFigureEditButtonRenderer(keyFigure));
	};

	my.updateUrl = function () {
		const params = {
			keyFigure: getSelectedKeyFigure() && getSelectedKeyFigure().getId(),
			periodLength: my.previousPeriodPicker.getSelected(),
		};

		const dimensionId = my.dimensionPicker.getSelectedDimensionId();
		if (dimensionId) {
			params["dimensionId"] = dimensionId;
		}

		my.redirectTo("single-kpi/", params);
	};

	my.renderCreateKeyFigureButtonOn = function (html) {
		if (!my.canCreateCustomKeyFigures()) {
			return;
		}

		html.render((html) =>
			html.a(
				{
					klass: "btn btn-default btn-block",
					"data-test": "create-custom-kpi",
					href: my.linkTo("kpi-editor"),
				},
				_("New custom financial KPI"),
			),
		);
	};

	my.renderCreateDriverKeyFigureButtonOn = function (html) {
		if (!my.canCreateCustomKeyFigures()) {
			return;
		}

		html.render((html) =>
			html.button(
				{
					click: () =>
						openDriverKeyFigureCreationDialog({
							onKeyFigureCreated: my.onKeyFigureCreated,
						}),
					class: "btn btn-default btn-block",
					"data-test": "create-driver-kpi",
				},
				_("New custom driver KPI"),
			),
		);
	};

	my.onKeyFigureCreated = async function (keyFigure) {
		await getKeyFigureService().create(keyFigure);
		await my.addToVisibleKeyFigures(keyFigure);
		my.redirectTo("kpi-editor", { keyFigureId: keyFigure.getId() });
	};

	my.addToVisibleKeyFigures = function (keyFigure) {
		visibleKeyFigures.setKeyFigures([
			...visibleKeyFigures.getKeyFigures(),
			keyFigure,
		]);

		return my.keyFigurePresetService.update(visibleKeyFigures, {
			notify: false,
		});
	};

	my.willDetach = function () {
		my.dimensionPicker.willDetach();
	};

	my.editKeyFigureDataForCurrentFiscalYear = async function () {
		const result = await my.keyFigureResultService.getValuesForPeriods({
			keyFigures: [getSelectedKeyFigure()],
			periods: session.getPeriod().getYear().asMonths(),
			dimensionId: my.dimensionPicker.getSelectedDimensionId(),
		});

		my.showEditKeyFigureValueDialog({
			values: result,
			dimensionName: my.dimensionPicker.getSelectedDimensionName(),
			action: (values) => {
				const data = Object.keys(values).map((key) => {
					const period = session.getCompany().findPeriodById(key);
					return new KeyFigureData({
						value: numbro.unformat(values[key]),
						keyFigureDataId: getSelectedKeyFigure().getId(),
						period,
						fiscalYearId: period.getYear().getId(),
						dimensionId:
							my.dimensionPicker.getSelectedDimensionId(),
					});
				});

				saveKeyFiguredata({
					keyFigureData: data,
					fiscalYearId: session.getPeriod().getYear().getId(),
				});
			},
		});
	};

	my.showEditKeyFigureValueDialog = function ({
		values,
		dimensionName,
		action = () => {},
	}) {
		const formatter = keyFigureValueFormatter();

		my.configure({
			contentCssClass: "edit-values",
			title: _("Edit Data for {dimensionName}", { dimensionName }),
			formSpec: {
				saveAction: action,
				validateOnBlur: true,
			},
			formBuilder: (form) => {
				values.getValues().forEach((value) => {
					form.input({
						label: value.getPeriod().toSmartString(),
						value: value.getValue() && formatter.format(value),
						attribute: value.getPeriod().toString(),
						// eslint-disable-next-line no-shadow -- bad practice, we should fix this
						validator: (value) => {
							if (isNullOrEmpty(value)) {
								return;
							}

							notBlank()(value);
							validNumber()(value);
						},
					});
				});
			},
		});
	};

	my.selectDefaultKeyFigure = function ({
		keyFigureId = getSelectedKeyFigure()?.getId(),
		presets,
	}) {
		const defaultKeyFigure =
			findKeyFigure(keyFigureId) || my.getDefaultKeyFigure({ presets });

		my.keyFigurePicker.selectKeyFigure(defaultKeyFigure);
	};

	my.getDefaultKeyFigure = function ({ presets }) {
		const overviewPreset = my.getOverviewPreset({ presets });

		return overviewPreset.getKeyFigures()[0];
	};

	my.getOverviewPreset = function ({ presets }) {
		return presets.find((preset) => preset.getId() === "Overview");
	};

	my.setupKeyFigureComponents = async function ({
		keyFigureResult,
		lastPeriods,
	}) {
		const interestingNodeValues =
			await my.keyFigureCalculationService.fetchResultsForFormulaNodes({
				periods: lastPeriods,
				formulaNodes: findInterestingNodes(
					getSelectedKeyFigure().getFormula(),
				),
				dimensionId: my.dimensionPicker.getSelectedDimensionId(),
			});

		my.keyFigureComponents.setup({
			keyFigureResult,
			interestingNodeValues,
		});
	};

	async function saveKeyFiguredata({ keyFigureData, fiscalYearId }) {
		await my.keyFigureDataRepository.save({
			model: keyFigureData,
			filter: {
				fiscalYearId,
			},
		});

		that.show();
	}

	function getSelectedKeyFigure() {
		const selection = my.keyFigurePicker.getSelectedItems();
		return selection.length > 0 ? selection[0] : undefined;
	}

	function findKeyFigure(id) {
		return visibleKeyFigures
			.getKeyFigures()
			.find((kpi) => kpi.getId() === id);
	}

	return that;
}

export default keyFigureDocument;
